import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Hero from '../components/Hero';
import SEO from '../components/SEO';
import CustomContactForm from '../components/CustomContactForm';
import { activitiesData } from '../data/activities';
import { FaWhatsapp, FaEnvelope, FaPhone, FaMapMarkerAlt } from 'react-icons/fa';

const Home = () => {
  const { t, i18n } = useTranslation();
  
  // Usar dados locais
  const activities = activitiesData.filter(a => a.active);

  return (
    <div>
      <SEO />
      
      {/* Hero Section with Carousel */}
      <Hero />

      {/* Activities Section */}
      <section id="activities" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('activities.title')}</h2>
            <p className="text-xl text-gray-600">{t('activities.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {activities.map((activity) => {
              const lang = i18n.language;
              const translation = activity.translations[lang] || activity.translations['pt'];
              
              return (
                <div 
                  key={activity.id} 
                  className="bg-white rounded-lg shadow-lg overflow-hidden transform hover:scale-105 transition-all duration-300"
                  data-testid="activity-card"
                >
                  <div className="relative h-64">
                    <img
                      src={activity.image_url}
                      alt={translation.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-60"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-white text-2xl font-bold drop-shadow-lg">{translation.title}</h3>
                    </div>
                  </div>

                  <div className="p-6">
                    <p className="text-gray-700 mb-4 line-clamp-3">
                      {translation.short_description || translation.description}
                    </p>

                    {activity.duration && (
                      <div className="flex items-center text-gray-600 mb-4">
                        <span className="text-sm">⏱️ {activity.duration}</span>
                      </div>
                    )}

                    <a
                      href={activity.booking_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full bg-blue-600 hover:bg-blue-700 text-white text-center py-3 rounded-lg font-semibold transition-colors"
                      data-testid="activity-book-button"
                    >
                      {t('activities.learnMore')} →
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="sobre" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">{t('about.title')}</h2>
              <p className="text-lg text-gray-700 mb-6">
                «Não queremos fazer um simples passeio de barco, um simples aluguer de uma canoa.
                Queremos que as pessoas desfrutem de uma série de coisas em simultâneo: que façam o passeio,
                mas acompanhados por alguém que conhece a zona e que tem conhecimentos fundamentados que lhe
                permitam explicar às pessoas onde estão, o que estão a ver, a história dos lugares.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                A isto, junta-se uma componente gastronómica, com degustação de produtos regionais,
                alguns deles quase exclusivos aqui de VRSA».
              </p>
              <p className="text-sm text-gray-600 italic">
                - Cláudio Tenório, Director geral executivo da Nautitours®
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://nautitours.pt/multimedia/no2.jpg"
                alt="Nó - Cão de Água Português"
                className="rounded-lg shadow-lg w-full h-40 object-cover"
              />
              <img
                src="https://nautitours.pt/multimedia/nautitours.jpg"
                alt="Nautitours"
                className="rounded-lg shadow-lg w-full h-40 object-cover"
              />
              <img
                src="https://nautitours.pt/multimedia/Nautitours-3.jpeg"
                alt="Nautitours"
                className="rounded-lg shadow-lg w-full h-40 object-cover"
              />
              <img
                src="https://nautitours.pt/multimedia/818.jpeg"
                alt="Nautitours"
                className="rounded-lg shadow-lg w-full h-40 object-cover"
              />
            </div>
          </div>

          {/* Nó Section */}
          <div className="mt-16 bg-blue-50 rounded-2xl p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl font-bold text-blue-900 mb-4">{t('about.noTitle')}</h3>
                <h4 className="text-xl text-blue-700 mb-4">{t('about.noSubtitle')}</h4>
                <p className="text-gray-700 mb-4">{t('about.noDescription')}</p>
                <p className="text-gray-700">
                  Entre muitas valiosíssimas funções acumuladas relacionadas com a marinharia,
                  o "Nó" destaca-se pelos seus dotes de salvamento marítimo, os quais são muito
                  apreciada por todos. Poderá vê-lo a realizar manobras de apoio no cais como o
                  segurar das embarcações ou o saltar do cais para devolver items caídos ao mar.
                </p>
              </div>
              <div className="flex justify-center">
                <img
                  src="https://nautitours.pt/multimedia/no.jpg"
                  alt="Nó - Cão de Água Português"
                  className="rounded-full shadow-xl w-48 h-48 object-cover border-4 border-white"
                />
              </div>
              </div>
            </div>
          </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('contact.title')}</h2>
            <p className="text-xl text-gray-600">{t('contact.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Custom Contact Form */}
            <CustomContactForm />

            {/* Contact Information */}
            <div>
              <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Informações de Contacto</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <FaMapMarkerAlt className="text-blue-600 text-xl mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Localização</h4>
                      <p className="text-gray-600">Vila Real de Santo António, Portugal</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <FaPhone className="text-blue-600 text-xl mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Telefone</h4>
                      <a href="tel:+351964423117" className="text-gray-600 hover:text-blue-600">
                        +351 964 423 117
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <FaEnvelope className="text-blue-600 text-xl mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Email</h4>
                      <a href="mailto:sales@nautitours.pt" className="text-gray-600 hover:text-blue-600">
                        sales@nautitours.pt
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* WhatsApp Contact */}
              <a
                href="https://api.whatsapp.com/send?phone=351964423117"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center space-x-3 bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors shadow-lg mb-8"
              >
                <FaWhatsapp className="text-3xl" />
                <span className="text-lg">{t('contact.whatsapp')}</span>
              </a>

              {/* Map */}
              <div className="rounded-lg overflow-hidden shadow-lg h-64">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3197.534!2d-7.414!3d37.194!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzfCsDExJzM4LjQiTiA3wrAyNCc1MC40Ilc!5e0!3m2!1spt!2spt!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Nautitours Location"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;